import type { ExportModel } from "./build.ts";

// Ported VERBATIM from VRMxShogun (src/fbx/asciiFbx.ts). Shogun compatibility
// was validated against this exact ASCII 7.4 (7400) output; do NOT reroute this
// through our binary writer, and do NOT change the emitted bytes.
//
// Minimal ASCII FBX 7.4 (7400) writer, scoped to the subset Vicon Shogun needs:
// a LimbNode skeleton (world-aligned, identity rotation), skinned meshes with
// skin clusters, a BindPose, and per-mesh phong materials (no textures in v1).
//
// Bones are emitted already rebaked by build.ts: every bone has identity
// rotation and a world position; the cluster matrices below follow from that
// (TransformLink = bone world bind, Transform = its inverse), and the BindPose
// matches TransformLink exactly. That identity-at-bind relationship is what
// keeps the mesh from exploding on import.

const num = (n: number): string =>
  Number.isFinite(n) ? (Object.is(n, -0) ? "0" : String(n)) : "0";

const arr = (xs: number[] | ArrayLike<number>): string => {
  const parts: string[] = [];
  for (let i = 0; i < xs.length; i++) parts.push(num(xs[i] as number));
  return parts.join(",");
};

function transformLink(p: [number, number, number]): number[] {
  // column-major, identity rotation, translation in last column
  return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, p[0], p[1], p[2], 1];
}

function transformInverse(p: [number, number, number]): number[] {
  return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, -p[0], -p[1], -p[2], 1];
}

export interface WriteOpts {
  /** Pre-rotate exported content +90° about X so it lands upright in a Z-up
   *  Shogun project (otherwise the Y-up FBX imports face-down). Preview is
   *  unaffected — this only changes the FBX. */
  rotate?: boolean;
}

export function writeFbx(
  model: ExportModel,
  idGen: () => number,
  opts: WriteOpts = {},
): string {
  const L: string[] = [];
  const w = (s = "") => L.push(s);

  // Rotation table: (x,y,z) -> (x,-z,y) is +90° about X. Apply once, up-front,
  // to bone world positions and to mesh vertices/normals.
  const rotPt = opts.rotate
    ? (p: number[]): [number, number, number] => [p[0], -p[2], p[1]]
    : (p: number[]): [number, number, number] => [p[0], p[1], p[2]];
  const rotFlat = (a: number[]): number[] => {
    if (!opts.rotate) return a;
    const out = new Array<number>(a.length);
    for (let i = 0; i < a.length; i += 3) {
      out[i] = a[i];
      out[i + 1] = -a[i + 2];
      out[i + 2] = a[i + 1];
    }
    return out;
  };
  const boneWp = model.bones.map((b) => rotPt(b.worldPos));
  const meshPos = model.meshes.map((m) => rotFlat(m.positions));
  const meshNrm = model.meshes.map((m) => rotFlat(m.normals));

  // ---- ids for non-bone objects -----------------------------------------
  const boneAttrId = model.bones.map(() => idGen());
  interface MeshIds {
    geo: number;
    mdl: number;
    mat: number;
    skin: number;
    clusters: number[];
  }
  const meshIds: MeshIds[] = model.meshes.map((m) => ({
    geo: idGen(),
    mdl: idGen(),
    mat: idGen(),
    skin: idGen(),
    clusters: m.clusters.map(() => idGen()),
  }));
  const poseId = idGen();

  // ---- header ------------------------------------------------------------
  const now = new Date();
  w("; FBX 7.4.0 project file");
  w("; Generated by VRMxShogun (https://github.com/vtoku/VRMxShogun)");
  w(
    "; ----------------------------------------------------------------------",
  );
  w("");
  w("FBXHeaderExtension:  {");
  w("\tFBXHeaderVersion: 1003");
  w("\tFBXVersion: 7400");
  w("\tCreationTimeStamp:  {");
  w("\t\tVersion: 1000");
  w(`\t\tYear: ${now.getFullYear()}`);
  w(`\t\tMonth: ${now.getMonth() + 1}`);
  w(`\t\tDay: ${now.getDate()}`);
  w(`\t\tHour: ${now.getHours()}`);
  w(`\t\tMinute: ${now.getMinutes()}`);
  w(`\t\tSecond: ${now.getSeconds()}`);
  w("\t\tMillisecond: 0");
  w("\t}");
  w('\tCreator: "VRMxShogun"');
  w("}");

  // ---- global settings (Y-up, cm) ---------------------------------------
  w("GlobalSettings:  {");
  w("\tVersion: 1000");
  w("\tProperties70:  {");
  w('\t\tP: "UpAxis", "int", "Integer", "",1');
  w('\t\tP: "UpAxisSign", "int", "Integer", "",1');
  w('\t\tP: "FrontAxis", "int", "Integer", "",2');
  w('\t\tP: "FrontAxisSign", "int", "Integer", "",1');
  w('\t\tP: "CoordAxis", "int", "Integer", "",0');
  w('\t\tP: "CoordAxisSign", "int", "Integer", "",1');
  w('\t\tP: "OriginalUpAxis", "int", "Integer", "",1');
  w('\t\tP: "OriginalUpAxisSign", "int", "Integer", "",1');
  w('\t\tP: "UnitScaleFactor", "double", "Number", "",1');
  w('\t\tP: "OriginalUnitScaleFactor", "double", "Number", "",1');
  w("\t}");
  w("}");

  // ---- documents ---------------------------------------------------------
  const docId = idGen();
  w("Documents:  {");
  w("\tCount: 1");
  w(`\tDocument: ${docId}, "", "Scene" {`);
  w("\t\tRootNode: 0");
  w("\t}");
  w("}");

  w("References:  {");
  w("}");

  // ---- definitions -------------------------------------------------------
  const modelCount = model.bones.length + model.meshes.length;
  const geoCount = model.meshes.length;
  const matCount = model.meshes.length;
  const attrCount = model.bones.length;
  let deformerCount = 0;
  for (const m of meshIds) deformerCount += 1 + m.clusters.length;
  const totalDefs = 1 + modelCount + geoCount + matCount + attrCount + deformerCount + 1;

  w("Definitions:  {");
  w("\tVersion: 100");
  w(`\tCount: ${totalDefs}`);
  w('\tObjectType: "GlobalSettings" {');
  w("\t\tCount: 1");
  w("\t}");
  w('\tObjectType: "Model" {');
  w(`\t\tCount: ${modelCount}`);
  w("\t}");
  w('\tObjectType: "NodeAttribute" {');
  w(`\t\tCount: ${attrCount}`);
  w("\t}");
  w('\tObjectType: "Geometry" {');
  w(`\t\tCount: ${geoCount}`);
  w("\t}");
  w('\tObjectType: "Material" {');
  w(`\t\tCount: ${matCount}`);
  w("\t}");
  w('\tObjectType: "Deformer" {');
  w(`\t\tCount: ${deformerCount}`);
  w("\t}");
  w('\tObjectType: "Pose" {');
  w("\t\tCount: 1");
  w("\t}");
  w("}");

  // ---- objects -----------------------------------------------------------
  w("Objects:  {");

  // geometries + mesh models + materials + skins + clusters
  model.meshes.forEach((m, mi) => {
    const ids = meshIds[mi];

    w(`\tGeometry: ${ids.geo}, "Geometry::${m.name}", "Mesh" {`);
    w(`\t\tVertices: *${m.positions.length} {`);
    w(`\t\t\ta: ${arr(meshPos[mi])}`);
    w("\t\t}");
    w(`\t\tPolygonVertexIndex: *${m.polygonVertexIndex.length} {`);
    w(`\t\t\ta: ${arr(m.polygonVertexIndex)}`);
    w("\t\t}");
    w("\t\tGeometryVersion: 124");
    w("\t\tLayerElementNormal: 0 {");
    w("\t\t\tVersion: 102");
    w('\t\t\tName: ""');
    w('\t\t\tMappingInformationType: "ByPolygonVertex"');
    w('\t\t\tReferenceInformationType: "Direct"');
    w(`\t\t\tNormals: *${m.normals.length} {`);
    w(`\t\t\t\ta: ${arr(meshNrm[mi])}`);
    w("\t\t\t}");
    w("\t\t}");
    w("\t\tLayerElementUV: 0 {");
    w("\t\t\tVersion: 101");
    w('\t\t\tName: "UVMap"');
    w('\t\t\tMappingInformationType: "ByPolygonVertex"');
    w('\t\t\tReferenceInformationType: "Direct"');
    w(`\t\t\tUV: *${m.uvs.length} {`);
    w(`\t\t\t\ta: ${arr(m.uvs)}`);
    w("\t\t\t}");
    w("\t\t}");
    w("\t\tLayerElementMaterial: 0 {");
    w("\t\t\tVersion: 101");
    w('\t\t\tName: ""');
    w('\t\t\tMappingInformationType: "AllSame"');
    w('\t\t\tReferenceInformationType: "IndexToDirect"');
    w("\t\t\tMaterials: *1 {");
    w("\t\t\t\ta: 0");
    w("\t\t\t}");
    w("\t\t}");
    w("\t\tLayer: 0 {");
    w("\t\t\tVersion: 100");
    w("\t\t\tLayerElement:  {");
    w('\t\t\t\tType: "LayerElementNormal"');
    w("\t\t\t\tTypedIndex: 0");
    w("\t\t\t}");
    w("\t\t\tLayerElement:  {");
    w('\t\t\t\tType: "LayerElementMaterial"');
    w("\t\t\t\tTypedIndex: 0");
    w("\t\t\t}");
    w("\t\t\tLayerElement:  {");
    w('\t\t\t\tType: "LayerElementUV"');
    w("\t\t\t\tTypedIndex: 0");
    w("\t\t\t}");
    w("\t\t}");
    w("\t}");

    // mesh model (baked to world -> identity local transform)
    w(`\tModel: ${ids.mdl}, "Model::${m.name}", "Mesh" {`);
    w("\t\tVersion: 232");
    w("\t\tProperties70:  {");
    w('\t\t\tP: "InheritType", "enum", "", "",1');
    w('\t\t\tP: "DefaultAttributeIndex", "int", "Integer", "",0');
    w('\t\t\tP: "Lcl Translation", "Lcl Translation", "", "A",0,0,0');
    w('\t\t\tP: "Lcl Rotation", "Lcl Rotation", "", "A",0,0,0');
    w('\t\t\tP: "Lcl Scaling", "Lcl Scaling", "", "A",1,1,1');
    w("\t\t}");
    w("\t\tShading: Y");
    w('\t\tCulling: "CullingOff"');
    w("\t}");

    // material
    const c = m.color;
    w(`\tMaterial: ${ids.mat}, "Material::${m.name}", "" {`);
    w("\t\tVersion: 102");
    w('\t\tShadingModel: "phong"');
    w("\t\tMultiLayer: 0");
    w("\t\tProperties70:  {");
    w(`\t\t\tP: "DiffuseColor", "Color", "", "A",${num(c[0])},${num(c[1])},${num(c[2])}`);
    w("\t\t}");
    w("\t}");

    // skin deformer
    w(`\tDeformer: ${ids.skin}, "Deformer::Skin ${m.name}", "Skin" {`);
    w("\t\tVersion: 101");
    w("\t\tLink_DeformAcuracy: 50");
    w("\t}");

    // clusters
    m.clusters.forEach((cl, ci) => {
      const bone = model.bones[cl.boneIndex];
      w(
        `\tDeformer: ${ids.clusters[ci]}, "SubDeformer::Cluster ${bone.name}", "Cluster" {`,
      );
      w("\t\tVersion: 100");
      w('\t\tUserData: "", ""');
      w(`\t\tIndexes: *${cl.indexes.length} {`);
      w(`\t\t\ta: ${arr(cl.indexes)}`);
      w("\t\t}");
      w(`\t\tWeights: *${cl.weights.length} {`);
      w(`\t\t\ta: ${arr(cl.weights)}`);
      w("\t\t}");
      w("\t\tTransform: *16 {");
      w(`\t\t\ta: ${arr(transformInverse(boneWp[cl.boneIndex]))}`);
      w("\t\t}");
      w("\t\tTransformLink: *16 {");
      w(`\t\t\ta: ${arr(transformLink(boneWp[cl.boneIndex]))}`);
      w("\t\t}");
      w("\t}");
    });
  });

  // bone node attributes + bone models
  model.bones.forEach((b, i) => {
    w(`\tNodeAttribute: ${boneAttrId[i]}, "NodeAttribute::", "LimbNode" {`);
    w("\t\tProperties70:  {");
    w('\t\t\tP: "Size", "double", "Number", "",1');
    w("\t\t}");
    w('\t\tTypeFlags: "Skeleton"');
    w("\t}");

    const wp = boneWp[i];
    const pwp = b.parentIndex >= 0 ? boneWp[b.parentIndex] : null;
    const lx = pwp ? wp[0] - pwp[0] : wp[0];
    const ly = pwp ? wp[1] - pwp[1] : wp[1];
    const lz = pwp ? wp[2] - pwp[2] : wp[2];

    w(`\tModel: ${b.id}, "Model::${b.name}", "LimbNode" {`);
    w("\t\tVersion: 232");
    w("\t\tProperties70:  {");
    w('\t\t\tP: "InheritType", "enum", "", "",1');
    w('\t\t\tP: "DefaultAttributeIndex", "int", "Integer", "",0');
    w(
      `\t\t\tP: "Lcl Translation", "Lcl Translation", "", "A",${num(lx)},${num(ly)},${num(lz)}`,
    );
    w('\t\t\tP: "Lcl Rotation", "Lcl Rotation", "", "A",0,0,0');
    w('\t\t\tP: "Lcl Scaling", "Lcl Scaling", "", "A",1,1,1');
    w('\t\t\tP: "PreRotation", "Vector3D", "Vector", "",0,0,0');
    w("\t\t}");
    w("\t\tShading: Y");
    w('\t\tCulling: "CullingOff"');
    w("\t}");
  });

  // bind pose
  const poseNodes: Array<{ id: number; mat: number[] }> = [];
  for (const ids of meshIds) {
    poseNodes.push({ id: ids.mdl, mat: transformLink([0, 0, 0]) });
  }
  model.bones.forEach((b, i) => {
    poseNodes.push({ id: b.id, mat: transformLink(boneWp[i]) });
  });
  w(`\tPose: ${poseId}, "Pose::BIND_POSES", "BindPose" {`);
  w('\t\tType: "BindPose"');
  w("\t\tVersion: 100");
  w(`\t\tNbPoseNodes: ${poseNodes.length}`);
  for (const pn of poseNodes) {
    w("\t\tPoseNode:  {");
    w(`\t\t\tNode: ${pn.id}`);
    w("\t\t\tMatrix: *16 {");
    w(`\t\t\t\ta: ${arr(pn.mat)}`);
    w("\t\t\t}");
    w("\t\t}");
  }
  w("\t}");

  w("}");

  // ---- connections -------------------------------------------------------
  w("Connections:  {");
  w('\t;Model::Scene root');
  // bones -> parent (or scene root)
  model.bones.forEach((b, i) => {
    const parentId = b.parentIndex >= 0 ? model.bones[b.parentIndex].id : 0;
    w(`\tC: "OO",${b.id},${parentId}`);
    w(`\tC: "OO",${boneAttrId[i]},${b.id}`);
  });
  // meshes
  model.meshes.forEach((m, mi) => {
    const ids = meshIds[mi];
    w(`\tC: "OO",${ids.mdl},0`);
    w(`\tC: "OO",${ids.geo},${ids.mdl}`);
    w(`\tC: "OO",${ids.mat},${ids.mdl}`);
    w(`\tC: "OO",${ids.skin},${ids.geo}`);
    m.clusters.forEach((cl, ci) => {
      const clusterId = ids.clusters[ci];
      const boneId = model.bones[cl.boneIndex].id;
      w(`\tC: "OO",${clusterId},${ids.skin}`);
      w(`\tC: "OO",${boneId},${clusterId}`);
    });
  });
  w("}");
  w("");

  return L.join("\n");
}
